/**
 * Générateur — Attendu 967 — Définir les parallélogrammes particuliers
 * (rectangle, losange, carré).
 *
 * Niveau : 5ème | Thème #11 Parallélogrammes (O26) | Domaine : Espace et géométrie
 * Source prof : cluster 2, Ex.3a + Créé n°40, extraction exerciseur/parallelogrammes.
 *
 * Cadrage : rectangle = parallélogramme avec un angle droit ; losange =
 * parallélogramme avec deux côtés consécutifs égaux ; carré = à la fois
 * rectangle et losange ; tous les carrés sont des losanges et des rectangles.
 *
 * 3 formats / 3 cartes :
 *  1. definition   — QCM : reconnaître la définition d'une forme tirée.
 *  2. demo_losange — Ex.3a : parallélogramme + 2 côtés consécutifs égaux → losange
 *                    (menus).
 *  3. familles     — Créé n°40 : inclusions des familles (tableau V/F), le piège
 *                    « tout rectangle est un carré » (FAUX) toujours présent.
 *
 * INVARIANTS : définitions et valeurs de vérité fixées par le cadrage, jamais
 * dépendantes du tirage (seuls les noms/formes/ordre varient).
 */

import {
    randInt, randomChoice, shuffle,
    makeQcm, makeTexteTrou, makeVraiFaux, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 967;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DEF = 'Définir un quadrilatère particulier';
const L_DEMO = 'Petite démonstration';
const L_FAMILLES = 'Les familles emboîtées';

const pick = (n) => shuffle('ABCDEFGHJKLMNPRSTUVWXYZ'.split('')).slice(0, n);

// ═══════════════════════════════════════════════════════════
// Carte 1 — Reconnaître la définition (QCM)
// ═══════════════════════════════════════════════════════════

const DEFINITIONS = {
    rectangle: {
        correct: 'un parallélogramme qui a un angle droit',
        distract: [
            'un parallélogramme qui a deux côtés consécutifs égaux',
            'un parallélogramme dont les diagonales sont perpendiculaires',
            'un quadrilatère dont les côtés opposés sont égaux',
        ],
    },
    losange: {
        correct: 'un parallélogramme qui a deux côtés consécutifs égaux',
        distract: [
            'un parallélogramme qui a un angle droit',
            'un parallélogramme qui a un angle droit et deux côtés consécutifs égaux',
            'un quadrilatère qui a deux côtés parallèles',
        ],
    },
    carré: {
        correct: 'un parallélogramme qui est à la fois un rectangle et un losange',
        distract: [
            'un parallélogramme qui a un angle droit',
            'un parallélogramme qui a deux côtés consécutifs égaux',
            'un quadrilatère qui a quatre côtés de la même longueur',
        ],
    },
};

const genDefinition = () => {
    const forme = randomChoice(Object.keys(DEFINITIONS));
    const { correct, distract } = DEFINITIONS[forme];
    const nom = pick(4).join('');
    const texte = `<p>${nom} est un ${forme}. Recopie et complète : un <b>${forme}</b> est…</p>`;
    const payload = makeQcm([correct, ...distract], correct, texte, { ...opts(L_DEF) });
    payload._meta.generatorKey = 'definition_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Petite démonstration : losange (Ex.3a), menus
// ═══════════════════════════════════════════════════════════

const genDemoLosange = () => {
    // Quadrilatère nommé n0-n1-n2-n3 ; deux côtés CONSÉCUTIFS égaux (au sommet k).
    const [n0, n1, n2, n3] = pick(4);
    const nom = `${n0}${n1}${n2}${n3}`;
    const noms = [n0, n1, n2, n3];
    const k = randInt(0, 3);
    const c1 = `${noms[(k + 3) % 4]}${noms[k]}`;   // côté (k-1 → k)
    const c2 = `${noms[k]}${noms[(k + 1) % 4]}`;    // côté (k → k+1)
    const phrase =
        `${nom} est un parallélogramme qui a {{deux côtés consécutifs égaux}} `
        + `(${c1} = ${c2}), donc c'est {{un losange}}.`;
    const distract = filtrerDistracteurs(phrase, [
        'deux côtés opposés égaux', 'un angle droit', 'des diagonales de même longueur',
        'un rectangle', 'un carré',
    ]);
    const texte = `<p>${nom} est un parallélogramme tel que ${c1} = ${c2}. `
        + `Complète la démonstration de sa nature :</p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DEMO), mode: 'drag', distracteurs: distract,
    });
    payload._meta.generatorKey = 'demo_losange_texte_trou_drag';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Les familles emboîtées (Créé n°40), tableau V/F
// ═══════════════════════════════════════════════════════════

// Pool d'énoncés d'inclusion, valeur de vérité FIXE (true = Vrai).
const PIEGE = { txt: 'Tout rectangle est un carré.', vrai: false };
const POOL_FAMILLES = [
    { txt: 'Tout carré est un rectangle.', vrai: true },
    { txt: 'Tout carré est un losange.', vrai: true },
    { txt: 'Tout losange ayant un angle droit est un carré.', vrai: true },
    { txt: 'Tout rectangle est un parallélogramme.', vrai: true },
    { txt: 'Un parallélogramme dont les diagonales sont perpendiculaires est un losange.', vrai: true },
    { txt: 'Tout losange est un parallélogramme.', vrai: true },
    { txt: 'Un rectangle dont deux côtés consécutifs sont égaux est un carré.', vrai: true },
    { txt: 'Un carré est un losange particulier.', vrai: true },
    { txt: 'Tout losange est un carré.', vrai: false },
    { txt: 'Tout parallélogramme est un rectangle.', vrai: false },
    { txt: 'Tout rectangle est un losange.', vrai: false },
    { txt: 'Tout parallélogramme est un losange.', vrai: false },
];

const genFamilles = () => {
    // 6 lignes : le piège (F) + 2 autres fausses + 3 vraies, mélangées.
    const faux = shuffle(POOL_FAMILLES.filter((e) => !e.vrai)).slice(0, 2);
    const vrai = shuffle(POOL_FAMILLES.filter((e) => e.vrai)).slice(0, 3);
    const lignesData = shuffle([PIEGE, ...faux, ...vrai]);
    const lignes = lignesData.map((e) => e.txt);
    // colonnes ['Vrai','Faux'] → 0 si vrai, 1 si faux.
    const bonnes = lignesData.map((e) => (e.vrai ? 0 : 1));
    const texte = '<p>Vrai ou faux ? Coche la bonne case pour chaque affirmation.</p>';
    const payload = makeVraiFaux(lignes, bonnes, texte, { ...opts(L_FAMILLES) });
    payload._meta.generatorKey = 'familles_tableau_vf';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    definition_qcm_unique: genDefinition,
    demo_losange_texte_trou_drag: genDemoLosange,
    familles_tableau_vf: genFamilles,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_drag: 'Menus', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'definition', label: L_DEF, formatsList: buildFormats('definition') },
    { id: 'demo_losange', label: L_DEMO, formatsList: buildFormats('demo_losange') },
    { id: 'familles', label: L_FAMILLES, formatsList: buildFormats('familles') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { genDefinition, genDemoLosange, genFamilles };
